import axios from 'axios'
import { isValidToken, refreshAccessToken, setSession } from './jwt'

const isOnLoginPage = () => {
  const path = window.location.pathname
  return path === '/login' || path === '/einloggen'
}

const redirectToLogin = () => {
  if (!isOnLoginPage()) {
    window.location.href = '/login'
  }
}

class AxiosInstanceGroup {
  #bffService

  /**
   * @method
   * @param {string} baseURL - base url for instance
   */
  #createInstance(baseURL) {
    return axios.create({ baseURL })
  }

  get bffService() {
    return this.#bffService
  }

  set bffService(baseURL) {
    this.#bffService = this.#createInstance(baseURL)
  }
}

export const axiosInstance = new AxiosInstanceGroup()
axiosInstance.bffService = window._env_.REACT_APP_BFF_SERVICE

export const bffService = axiosInstance.bffService

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  failedQueue = []
}

// Request interceptor: Check if token is expired and refresh before request
bffService.interceptors.request.use(
  async (config) => {
    const accessToken = localStorage.getItem('accessToken')
    const refreshToken = localStorage.getItem('refreshToken')
    config.headers.product = 'COMMUNITY'
    // Skip token check for login and refresh endpoints
    if (
      config.url === '/login' ||
      config.url === '/users/refresh' ||
      !accessToken
    ) {
      return config
    }

    // Check if token is expired or about to expire (within 60 seconds)
    if (!isValidToken(accessToken)) {
      if (refreshToken && !isRefreshing) {
        isRefreshing = true
        try {
          const { access_token } = await refreshAccessToken(refreshToken)
          config.headers.Authorization = `Bearer ${access_token}`
          processQueue(null, access_token)
        } catch (error) {
          processQueue(error, null)
          setSession(null, null)
          redirectToLogin()
          return Promise.reject(error)
        } finally {
          isRefreshing = false
        }
      } else {
        // No refresh token or already refreshing
        if (!isRefreshing) {
          setSession(null, null)
          redirectToLogin()
        }
      }
    } else {
      // Token is valid, set it in the request
      config.headers.Authorization = `Bearer ${accessToken}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor: Handle 401 errors and refresh token
bffService.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem('refreshToken')

      // Skip refresh for auth endpoints (401 here means refresh/login failed — do not retry refresh)
      if (
        originalRequest.url === '/login' ||
        originalRequest.url === '/users/refresh'
      ) {
        return Promise.reject(
          (error.response && error.response.data) || 'Something went wrong'
        )
      }

      // If we're already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            return bffService(originalRequest)
          })
          .catch((err) => {
            return Promise.reject(err)
          })
      }

      // Attempt to refresh token
      if (refreshToken) {
        originalRequest._retry = true
        isRefreshing = true

        try {
          const { access_token } = await refreshAccessToken(refreshToken)
          originalRequest.headers.Authorization = `Bearer ${access_token}`
          processQueue(null, access_token)

          // Retry the original request
          return bffService(originalRequest)
        } catch (refreshError) {
          processQueue(refreshError, null)
          setSession(null, null)
          redirectToLogin()
          return Promise.reject(refreshError)
        } finally {
          isRefreshing = false
        }
      } else {
        // No refresh token available, clear session and redirect
        setSession(null, null)
        redirectToLogin()
      }
    }

    return Promise.reject(
      (error.response && error.response.data) || 'Something went wrong'
    )
  }
)

export default axiosInstance
